'use client';

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { apiFetch } from './api';

export type FontSize = '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | '11' | '12' | '13' | '14' | '15';

export const FONT_SIZE_PX: Record<FontSize, number> = {
  '1': 14,
  '2': 15,
  '3': 16,
  '4': 17,
  '5': 18,
  '6': 19,
  '7': 20,
  '8': 22,
  '9': 24,
  '10': 26,
  '11': 28,
  '12': 30,
  '13': 32,
  '14': 34,
  '15': 36,
};

const VALID_SIZES: FontSize[] = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15'];

function isValidSize(s: string | null | undefined): s is FontSize {
  return !!s && VALID_SIZES.includes(s as FontSize);
}

function applyFontSize(size: FontSize) {
  document.documentElement.style.fontSize = `${FONT_SIZE_PX[size]}px`;
}

type FontSizeContextType = {
  fontSize: FontSize;
  setFontSize: (size: FontSize) => void;
};

const FontSizeContext = createContext<FontSizeContextType | undefined>(undefined);

export function FontSizeProvider({ children }: { children: ReactNode }) {
  // Initialize from localStorage synchronously to match the inline script in layout.tsx.
  const [fontSize, setFontSizeState] = useState<FontSize>(() => {
    if (typeof window === 'undefined') return '4';
    const stored = window.localStorage.getItem('fontSize');
    return isValidSize(stored) ? (stored as FontSize) : '4';
  });

  // Step 1: Ensure DOM matches state (inline script already did this, but be safe)
  useEffect(() => {
    applyFontSize(fontSize);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Step 2: If no localStorage preference, try user profile from DB
  useEffect(() => {
    const stored = window.localStorage.getItem('fontSize');
    if (isValidSize(stored)) return; // User has own preference

    const email = window.localStorage.getItem('email');
    if (email) {
      apiFetch<{ fontSizePreference?: string }>('/api/auth/me', { silent: true })
        .then((profile) => {
          if (isValidSize(profile?.fontSizePreference)) {
            setFontSizeState(profile.fontSizePreference);
            applyFontSize(profile.fontSizePreference);
          }
        })
        .catch((e) => console.error("[api]", e));
    }
  }, []);

  const setFontSize = (newSize: FontSize) => {
    setFontSizeState(newSize);
    window.localStorage.setItem('fontSize', newSize);
    applyFontSize(newSize);

    // Persist to DB if logged in
    const email = window.localStorage.getItem('email');
    if (email) {
      apiFetch('/api/auth/font-size', {
        method: 'POST',
        body: JSON.stringify({ fontSize: newSize }),
      }).catch((e) => console.error("[api]", e));
    }
  };

  return (
    <FontSizeContext.Provider value={{ fontSize, setFontSize }}>
      {children}
    </FontSizeContext.Provider>
  );
}

export function useFontSize() {
  const context = useContext(FontSizeContext);
  if (!context) {
    throw new Error('useFontSize must be used within a FontSizeProvider');
  }
  return context;
}
